import { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';

interface StopwatchProps {
  onTimeUpdate?: (time: number) => void;
  value?: number;
  isRunning?: boolean;
  onRunningChange?: (isRunning: boolean) => void;
  label?: string;
  hideDisplay?: boolean;
}

export function Stopwatch({ onTimeUpdate, value = 0, isRunning: externalIsRunning, onRunningChange, label, hideDisplay }: StopwatchProps) {
  const [time, setTime] = useState(value);
  const [internalIsRunning, setInternalIsRunning] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const startTimeRef = useRef<number>(0);
  const prevValueRef = useRef(value);
  
  // Use external isRunning if provided, otherwise use internal state
  const isRunning = externalIsRunning !== undefined ? externalIsRunning : internalIsRunning;

  // Keep the ref updated
  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);

  // Sync with external value changes ONLY when value actually changes from external source
  // Don't sync while timer is running (to avoid circular updates)
  useEffect(() => {
    if (value !== prevValueRef.current && !isRunning) {
      setTime(value);
      prevValueRef.current = value;
      if (value === 0) {
        startTimeRef.current = 0;
      }
    }
  }, [value, isRunning]);

  useEffect(() => {
    if (isRunning) {
      // Set the start time when timer begins
      startTimeRef.current = Date.now() - time * 1000;
      
      intervalRef.current = window.setInterval(() => {
        const newTime = (Date.now() - startTimeRef.current) / 1000;
        setTime(newTime);
        onTimeUpdateRef.current?.(newTime);
      }, 10);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning]);

  const handleStartStop = () => {
    if (externalIsRunning === undefined) {
      setInternalIsRunning(!internalIsRunning);
    } else {
      onRunningChange?.(!isRunning);
    }
  };

  const handleReset = () => {
    if (externalIsRunning === undefined) {
      setInternalIsRunning(false);
    } else {
      onRunningChange?.(false);
    }
    setTime(0);
    onTimeUpdateRef.current?.(0);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const centisecs = Math.floor((seconds % 1) * 100);
    
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}.${centisecs.toString().padStart(2, '0')}`;
    }
    return `${secs}.${centisecs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col gap-3">
      {!hideDisplay && (label ? (
        <div className="mb-3">
          <label className="text-sm font-medium">{label}</label>
          <div className="font-mono text-3xl font-bold text-blue-600 text-center">
            {formatTime(time)}s
          </div>
        </div>
      ) : (
        <div className="font-mono text-3xl font-bold text-blue-600 text-center">
          {formatTime(time)}s
        </div>
      ))}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleStartStop}
          className={`flex-1 py-6 px-8 rounded-lg transition-colors flex items-center justify-center gap-2 ${
            isRunning
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
          title={isRunning ? 'Stop' : 'Start'}
        >
          {isRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
          <span className="font-semibold">{isRunning ? 'Stop' : 'Start'}</span>
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="px-4 py-6 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
          title="Reset"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}